import * as Sentry from "shared/providers/sentry/sentry.package";

import i18next from "shared/providers/i18next/i18next.package";

import { toast } from "shared/providers/toast/toast.package";
import { ApolloLink } from "shared/providers/apollo/apollo.package";
import { onError } from "shared/providers/apollo/link/error.package";

import { removeLocalStorageItem } from "shared/utils/local-storage";

import isInvalidSentryGqlCode from "shared/utils/is-invalid-sentry-gql-code";

import { WEB_AUTH_TOKEN } from "shared/common/data/constants";

// fetch() rejects with a different message per browser when the API can't be reached
const UNREACHABLE_MESSAGES = [
  "Failed to fetch", // Chrome, Edge
  "Load failed", // Safari
  "NetworkError when attempting to fetch resource", // Firefox
  "CORS",
];

const UNAVAILABLE_STATUS_CODES = [502, 503, 504];

const CONNECTION_LOST_TOAST_ID = "connection-lost";

// Until the API has answered once there is nothing on screen worth keeping, so
// an unreachable API gets the full-page screen. After that a blip only gets a
// toast, so it doesn't throw away what the user is in the middle of.
let hasReachedApi = false;

const connectionLink = new ApolloLink((operation, forward) =>
  forward(operation).map((result) => {
    hasReachedApi = true;

    return result;
  })
);

const errorLink = (setIsMaintenance: (maintenance: boolean) => void) =>
  onError(({ graphQLErrors, networkError, operation, forward }) => {
    if (graphQLErrors) {
      for (let err of graphQLErrors) {
        const code =
          err.extensions &&
          err.extensions.code &&
          typeof err.extensions.code === "string"
            ? err.extensions.code
            : null;

        const isInvalidCode = isInvalidSentryGqlCode(code);

        if (!isInvalidCode) {
          Sentry.withScope((scope) => {
            scope.setTag("errorType", "graphql");
            scope.setContext("graphql", {
              operationName: operation.operationName,
              variables: operation.variables,
            });
            scope.setFingerprint([operation.operationName, err.message]);

            const transformedErr = new Error(err.message);

            transformedErr.name = `GraphQL Error: ${operation.operationName}`;

            Sentry.captureException(transformedErr);
          });
        }

        if (code === "UNAUTHENTICATED") {
          removeLocalStorageItem(WEB_AUTH_TOKEN);

          const queryString = window.location.search;
          let loginToken: string | null = null;

          if (queryString) {
            const urlParams = new URLSearchParams(queryString);
            loginToken = urlParams.get("loginToken");
          }

          if (loginToken) {
            window.location.replace(window.location.pathname);
          } else {
            window.location.reload();
          }
        }
      }
    } else if (networkError) {
      console.log(networkError);

      if ("statusCode" in networkError) {
        console.log("Status Code:", networkError.statusCode);
      }

      // 4403 is the API refusing the socket's token: the session was revoked
      // (logout, password reset, deactivation) or expired. That is not a
      // connection problem, and the next request comes back UNAUTHENTICATED,
      // which signs the user out above. (When the API cannot check the token
      // at all, it closes with 1013 instead, which still shows the toast.)
      const sessionEnded = networkError.message.includes(
        "Socket closed with event 4403"
      );

      if (networkError.message.includes("Socket closed") && !sessionEnded) {
        toast.error(
          `An error occurred while establishing connection with socket. In-app notifications might not work. Error: ${networkError.message}`
        );
      }

      if (
        UNREACHABLE_MESSAGES.some((message) =>
          networkError.message.includes(message)
        ) ||
        ("statusCode" in networkError &&
          UNAVAILABLE_STATUS_CODES.includes(networkError.statusCode))
      ) {
        if (hasReachedApi) {
          toast.error(i18next.t("global.connectionLostToast"), {
            toastId: CONNECTION_LOST_TOAST_ID,
          });
        } else {
          setIsMaintenance(true);
        }
      }
    }
  }).concat(connectionLink);

export default errorLink;
